/**
 * @file add-new-customers.tsx
 * @module app/modules/customers/add-new-customers
 * @description Customer creation page in UTrade Admin.
 * Captures customer profile and business details, resolves address metadata, and submits new customer records.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { Button, Card, Col, Form, InputGroup, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { fileValidation } from '../../../Global/fileValidation';
import EditSVG from '../../../_admin/assets/media/svg_uTrade/edit-round.svg';
import uploadImg from '../../../_admin/assets/media/upload.png';
import APICallService from '../../../api/apiCallService';
import { isValidTin, TIN_ERROR, tinInput } from './customer-tin';
import {
  buyer,
  customerService,
  master,
  customerCategory,
} from '../../../api/apiEndPoints';
import { customerJSON } from '../../../api/apiJSON/customer';
import { CustomSelectWhite } from '../../custom/Select/CustomSelectWhite';
import AutoComplete from '../../custom/autoComplete';
import { Add, AllCustomers } from '../../../utils/constants';
import Method from '../../../utils/methods';
import { searchGoogleAddressUsingLatLong } from '../../../utils/googleAddress';
import { useAuth } from '../auth';
import { useWarehouse } from '../../context/WarehouseContext';
import { success } from '../../../Global/toast';

const AddNewCustomers = () => {
  const { selectedWarehouse } = useWarehouse();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [customerData, setCustomerData] = useState<{
    name: string;
    email: string;
    businessName: string;
    district: string;
    districtName: string;
    address: string;
    vatNumber: string;
    tinNumber: any;
    image: any;
    imageReader: any;
    types: string;
    phoneNumber: string;
    phoneCountry: string;
    lat: string;
    lng: string;
    category: any;
    isPharmacyVendor: boolean;
  }>({
    name: '',
    email: '',
    address: '',
    vatNumber: '',
    tinNumber: '',
    image: '',
    businessName: '',
    district: '',
    districtName: '',
    imageReader: {},
    types: '',
    phoneNumber: '',
    phoneCountry: '+255',
    lat: '1',
    lng: '1',
    category: '',
    isPharmacyVendor: false,
  });
  const [customerValidation, setCustomerValidation] = useState<{
    name: boolean;
    businessName: boolean;
    address: boolean;
    types: boolean;
    phoneNumber: boolean;
    tinNumber: boolean;
    vatNumber: boolean;
  }>({
    name: false,
    address: false,
    businessName: false,
    types: false,
    phoneNumber: false,
    tinNumber: false,
    vatNumber: false,
  });
  const [businessTypes, setBusinessTypes] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [districts, setDistricts] = useState([]);
  const [customerCategoryData, setCustomerCategoryData] = useState([]);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    (async () => {
      if (!Method.hasPermission(AllCustomers, Add, currentUser)) {
        return window.history.back();
      }
      await fetchInitData();
      await fetchCustomerCategory();
      await fetchDistrict();
    })();
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */
  const fetchInitData = async () => {
    setFetchLoading(true);
    let businessApiService = new APICallService(
      master.listBusinessTypes,
      {
        'addedFor[1]': 2,
      },
      '',
      '',
      '',
      '',
      AllCustomers,
      selectedWarehouse?.value || null
    );
    let businessResponse = await businessApiService.callAPI();
    if (businessResponse) {
      setBusinessTypes(businessResponse.records);
    }
    setFetchLoading(false);
  };
  const fetchCustomerCategory = async () => {
    let params: any = {
      needCount: true,
    };
    if (selectedWarehouse?.value) {
      params.warehouse = selectedWarehouse?.value;
    }
    let apiService = new APICallService(
      customerCategory?.listCustomerCategory,
      params,
      '',
      '',
      '',
      '',
      '',
      selectedWarehouse?.value || null
    );
    let response = await apiService.callAPI();
    let data: any = [...customerCategoryData];
    if (response?.records?.length) {
      response?.records?.forEach((val: any) => {
        data?.push({
          value: val?._id,
          label: val?.title,
          title: val?.title,
        });
      });
    }
    setCustomerCategoryData(data);
  };
  const handleCategorySelect = (id: string) => {
    let temp: any = { ...customerData };
    temp.category = id;
    setCustomerData(temp);
  };
  const fetchDistrict = async () => {
    let params: any = {};
    if (selectedWarehouse?.value) {
      params.warehouse = selectedWarehouse?.value;
    }
    let apiService = new APICallService(
      customerService.listDistrict,
      params,
      '',
      '',
      '',
      '',
      AllCustomers,
      selectedWarehouse?.value || null
    );
    let response = await apiService.callAPI();
    setDistricts(response?.records);
  };
  const isAlphaNumeric = (value: string) => {
    return /^[a-zA-Z0-9]*$/.test(value || '');
  };

  const handleBasicDetailsChange = (name: string, value: string) => {
    let temp: any = { ...customerData };
    let tempValidation: any = { ...customerValidation };
    temp[name] = value.trimStart();

    if (name === 'tinNumber') {
      temp[name] = tinInput(value);
      tempValidation[name] = !isValidTin(temp[name]);
    } else if (name === 'vatNumber') {
      tempValidation[name] = !!value?.length && !isAlphaNumeric(value);
    } else if (Object?.keys(tempValidation)?.includes(name)) {
      tempValidation[name] = false;
      if (!value?.length) tempValidation[name] = true;
    }
    setCustomerData(temp);
    setCustomerValidation(tempValidation);
  };
  const handleBusinessTypesSelect = (id: string) => {
    let temp: any = { ...customerData };
    let tempValidation: any = { ...customerValidation };
    temp.types = id;
    tempValidation.types = false;
    setCustomerData(temp);
    setCustomerValidation(tempValidation);
  };
  const handlePharmacyToggle = (checked: boolean) => {
    let temp: any = { ...customerData };
    temp.isPharmacyVendor = checked;
    setCustomerData(temp);
  };
  const handleImageChange = (event: any) => {
    const selectedFiles = event?.target?.files;
    let temp = { ...customerData };
    if (!selectedFiles) return;
    else {
      if (fileValidation(selectedFiles?.[0])) {
        temp.image = URL.createObjectURL(selectedFiles?.[0]);
        temp.imageReader = selectedFiles?.[0];
      }
    }
    setCustomerData(temp);
  };

  /**
   * Handles the change of address input by the user. It searches for the address using
   * Google Maps API and updates the customer data accordingly.
   *
   * @param value The address input by the user
   * @param lat The latitude of the address
   * @param lng The longitude of the address
   */
  const handleAddressChange = async (
    value: string,
    lat: string,
    lng: string
  ) => {
    const googleAddress = await searchGoogleAddressUsingLatLong(lat, lng, {
      searchText: value,
      districts,
    });
    const selectedAddress = googleAddress?.data?.[0];
    const normalizedAddress = value?.trimStart();

    // Update the customer data with the selected address details and coordinates
    setCustomerData((prev: any) => ({
      ...prev,
      wardName: selectedAddress?.wardName || 'NA',
      localityName: selectedAddress?.localityName || 'NA',
      countryName: selectedAddress?.countryName || '',
      districtName: selectedAddress?.districtName || 'NA',
      district: selectedAddress?.district || 'NA',
      address: normalizedAddress,
      lat,
      lng,
    }));

    // Update the validation state for district and address based on the selected address and normalized input
    setCustomerValidation((prev: any) => ({
      ...prev,
      district: !selectedAddress?.status,
      address: !normalizedAddress?.length,
    }));
  };

  const handleAddCustomer = async () => {
    let temp = { ...customerData };
    let validateTemp = { ...customerValidation };
    if (temp?.name === '') {
      validateTemp.name = true;
    }
    if (temp?.businessName === '') {
      validateTemp.businessName = true;
    }
    if (temp?.phoneNumber === '') {
      validateTemp.phoneNumber = true;
    }
    if (temp?.address === '') {
      validateTemp.address = true;
    }
    if (temp?.types === '') {
      validateTemp.types = true;
    }
    if (!isValidTin(temp?.tinNumber)) {
      validateTemp.tinNumber = true;
    }
    if (temp?.vatNumber && !isAlphaNumeric(temp.vatNumber)) {
      validateTemp.vatNumber = true;
    }

    if (Object?.values(validateTemp)?.every((val) => val === false)) {
      const matchingObjects: any = districts?.filter((obj: any) => {
        // we're checking if 'searchText' is present in the 'name' property
        return obj?.name?.includes(temp?.districtName);
      });
      if (matchingObjects?.length) {
        temp.districtName = matchingObjects[0]?.name;
        temp.district = matchingObjects[0]?._id;
      } else {
        temp.districtName = 'UpCountry';
        temp.district = '651d5fa8317c6e0dcdcd8fa1';
      }
      setLoading(true);
      let apiService = new APICallService(
        buyer.addBuyer,
        customerJSON.addCustomer(temp),
        '',
        '',
        '',
        '',
        AllCustomers,
        selectedWarehouse?.value || null
      );
      let response = await apiService.callAPI();
      if (response) {
        success('Customer added successfully');
        navigate('/customers/view-customers');
        
      }
      setLoading(false);
    }
    setCustomerValidation(validateTemp);
  };

  return (
    <div className="p-5">
      <Row className="mb-6">
        <Col xs={12}>
          <h1 className="fs-22 fw-bolder">Add new customers</h1>
        </Col>
      </Row>
      <Row>
        <Col md={4} className="mb-lg-0 mb-8">
          <Card className="border bg-light">
            <Card.Body>
              <Row>
                <Col xs={12}>
                  <h5 className="fs-22 fw-bolder mb-6">Business logo</h5>
                </Col>
                <Col xs={12} className="text-md-center">
                  {customerData?.image ? (
                    <Col xs={12} className="text-md-center">
                      <div className="border border-r10px bg-white p-1">
                        <div className="image-input image-input-outline min-w-xl-260px min-h-xl-150px">
                          <div
                            className="image-input-wrapper shadow-none bgi-contain w-100 h-100 bgi-position-center"
                            style={{
                              // backgroundImage: `url(${sellerData.image})`,
                              backgroundRepeat: `no-repeat`,
                            }}
                          >
                            {customerData?.image && (
                              <img
                                width={262}
                                height={149}
                                src={customerData?.image}
                                className="img-fluid mh-150px border-r10px object-fit-contain"
                                alt=""
                              />
                            )}
                          </div>
                          <label
                            className="btn btn-icon btn-circle btn-active-color-primary w-50px h-50px "
                            data-kt-image-input-action="change"
                            title=""
                          >
                            <img src={EditSVG} alt="" height={33} width={33} />
                            <input
                              type="file"
                              name="avatar"
                              accept=".png, .jpg, .jpeg"
                              onChange={(event) => {
                                handleImageChange(event);
                              }}
                            />
                            <input type="hidden" name="avatar_remove" />
                          </label>
                        </div>
                      </div>
                    </Col>
                  ) : (
                    <Col xs={12} className="text-md-center">
                      <div className="upload-btn-wrapper">
                        <div className="symbol symbol-md-235px">
                          <img
                            src={uploadImg}
                            className="img-fluid min-h-200px min-w-200px"
                            alt=""
                          />
                        </div>
                        <input
                          className="w-100 h-100"
                          type="file"
                          name="myfile"
                          onChange={(event) => {
                            handleImageChange(event);
                          }}
                        />
                      </div>
                    </Col>
                  )}
                  <span className="text-gray text-italic">{`Image dimensions:-(700x700px)`}</span>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
        <Col md={8}>
          <Card className="bg-light pt-2 mb-6 border">
            <Card.Header className="border-bottom-0">
              <Card.Title>
                <h5 className="fs-22 fw-bolder">Customer details</h5>
              </Card.Title>
            </Card.Header>
            <Card.Body className="pt-0 pb-5">
              <Row className="align-items-center">
                <Col>
                  <Row>
                    <Col md={6} className="mb-3">
                      <Form.Group
                        className="mb-3"
                        controlId="exampleForm.ControlInput1"
                      >
                        <Form.Label className="fs-16 fw-500 text-dark required">
                          Name
                        </Form.Label>
                        <Form.Control
                          className={clsx(
                            'form-control-custom bg-white',
                            customerValidation.name ? 'border-danger' : ''
                          )}
                          type="text"
                          placeholder="Type here…"
                          onChange={(event: any) => {
                            handleBasicDetailsChange(
                              'name',
                              event.target.value
                            );
                          }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                      <Form.Group
                        className="mb-3"
                        controlId="exampleForm.ControlInput1"
                      >
                        <Form.Label className="fs-16 fw-500 text-dark ">
                          Email
                        </Form.Label>
                        <Form.Control
                          className={clsx('form-control-custom bg-white')}
                          type="email"
                          placeholder="Type here…"
                          onChange={(event: any) => {
                            handleBasicDetailsChange(
                              'email',
                              event.target.value
                            );
                          }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                      <div className="mb-3">
                        <Form.Label className="fs-16 fw-500 text-dark required">
                          Phone number
                        </Form.Label>
                        <InputGroup>
                          <InputGroup.Text
                            className={clsx(
                              'bg-white border-right-0 fs-16 fw-600 text-black px-6',
                              customerValidation.phoneNumber
                                ? 'border-danger'
                                : ''
                            )}
                          >
                            +255
                          </InputGroup.Text>
                          <Form.Control
                            name="phoneNumber"
                            className={clsx(
                              'form-control-custom border-active-none bg-white border-left-0 ps-0',
                              customerValidation.phoneNumber
                                ? 'border-danger'
                                : ''
                            )}
                            aria-label="Default"
                            aria-describedby="inputGroup-sizing-default"
                            onChange={(event: any) => {
                              handleBasicDetailsChange(
                                'phoneNumber',
                                event.target.value
                              );
                            }}
                          />
                        </InputGroup>
                      </div>
                    </Col>
                    <Col md={6} className="mb-3">
                      <Form.Group
                        className="mb-3"
                        controlId="exampleForm.ControlInput1"
                      >
                        <div>
                          <Form.Label className="fs-16 fw-500">
                            Customer Category
                          </Form.Label>
                        </div>
                        <CustomSelectWhite
                          onChange={(event: any) => {
                            handleCategorySelect(event.value);
                          }}
                          border={customerValidation.types ? '#e55451' : ''}
                          loading={fetchLoading}
                          isDisabled={fetchLoading}
                          options={customerCategoryData}
                          isMulti={false}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Card.Body>
          </Card>
          <Card className="bg-light mb-6 mb-xl-9 border">
            <Card.Header className="border-bottom-0">
              <Card.Title>
                <h5 className="fs-22 fw-bolder">Business details</h5>
              </Card.Title>
            </Card.Header>
            <Card.Body className="pt-0 pb-5">
              <Row className="align-items-center">
                <Col>
                  <Row>
                    <Col md={6} className="mb-3">
                      <Form.Group
                        className="mb-3"
                        controlId="exampleForm.ControlInput1"
                      >
                        <Form.Label className="fs-16 fw-500 text-dark required">
                          Name
                        </Form.Label>
                        <Form.Control
                          className={clsx(
                            'form-control-custom bg-white',
                            customerValidation.businessName
                              ? 'border-danger'
                              : ''
                          )}
                          type="text"
                          placeholder="Type here…"
                          onChange={(event: any) => {
                            handleBasicDetailsChange(
                              'businessName',
                              event.target.value
                            );
                          }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                      <Form.Group
                        className="mb-3"
                        controlId="exampleForm.ControlInput1"
                      >
                        <div>
                          <Form.Label className="fs-16 fw-500 required">
                            Type{' '}
                          </Form.Label>
                          {businessTypes.length &&
                            businessTypes
                              .filter((val: any) => {
                                return val._id === customerData.types;
                              })
                              .map((val: any) => {
                                return val.abbreviation ? (
                                  <span className="text-gray ms-2">
                                    {`(${val.abbreviation})`}
                                  </span>
                                ) : (
                                  <></>
                                );
                              })}
                        </div>
                        <CustomSelectWhite
                          onChange={(event: any) => {
                            handleBusinessTypesSelect(event._id);
                          }}
                          border={customerValidation.types ? '#e55451' : ''}
                          loading={fetchLoading}
                          isDisabled={fetchLoading}
                          options={
                            businessTypes?.length
                              ? businessTypes?.map((catval: any) => {
                                  return {
                                    value: catval?.title,
                                    label: catval?.title,
                                    _id: catval?._id,
                                    image: catval?.image,
                                    title: catval?.title,
                                  };
                                })
                              : []
                          }
                          isMulti={false}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                      <Form.Group
                        className="mb-1"
                        controlId="signupPharmacySwitch"
                      >
                        <Form.Label className="fs-16 fw-500 text-dark">
                          Sign Up As Pharmacy
                        </Form.Label>
                        <Form.Check
                          type="switch"
                          id="signup-pharmacy-switch"
                          // label={customerData?.isPharmacyVendor ? "True" : "False"}
                          checked={customerData?.isPharmacyVendor}
                          onChange={(event: any) => {
                            handlePharmacyToggle(event?.target?.checked);
                          }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                      <Form.Group
                        className="mb-3"
                        controlId="exampleForm.ControlInput1"
                      >
                        <Form.Label className="fs-16 fw-500 text-dark required">
                          Address
                        </Form.Label>
                        <AutoComplete
                          address={customerData?.address}
                          handleAddressChange={handleAddressChange}
                          lat={customerData?.lat}
                          lng={customerData?.lng}
                          border={clsx(
                            customerValidation?.address ? '#e55451' : ''
                          )}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                      <Form.Group
                        className="mb-3"
                        controlId="exampleForm.ControlInput1"
                      >
                        <Form.Label className="fs-16 fw-500 text-dark">
                          TIN Number
                        </Form.Label>
                        <Form.Control
                          className={clsx(
                            'form-control-custom bg-white',
                            customerValidation.tinNumber ? 'border-danger' : ''
                          )}
                          type="text"
                          inputMode="numeric"
                          placeholder="9 digits, e.g. 123456789"
                          value={customerData?.tinNumber}
                          onChange={(event: any) => {
                            handleBasicDetailsChange(
                              'tinNumber',
                              event.target.value
                            );
                          }}
                        />
                        {customerValidation.tinNumber ? (
                          <span className="text-danger fs-12">
                            {TIN_ERROR}
                          </span>
                        ) : (
                          <></>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                      <Form.Group
                        className="mb-3"
                        controlId="exampleForm.ControlInput1"
                      >
                        <Form.Label className="fs-16 fw-500 text-dark">
                          VAT Registration Number(VRN)
                        </Form.Label>
                        <Form.Control
                          className={clsx(
                            'form-control-custom bg-white',
                            customerValidation.vatNumber ? 'border-danger' : ''
                          )}
                          type="text"
                          placeholder="Type here…"
                          value={customerData?.vatNumber?.toUpperCase()}
                          onChange={(event: any) => {
                            handleBasicDetailsChange(
                              'vatNumber',
                              event.target.value
                            );
                          }}
                        />
                        {customerValidation.vatNumber ? (
                          <span className="text-danger fs-12">
                            VAT Number should only contain letters and numbers
                          </span>
                        ) : (
                          <></>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Card.Body>
          </Card>
          <div className="d-flex justify-content-end">
            <Button className="uw-btn uw-btn-primary uw-btn-lg"
              size="lg"
              data-analytics-event="customers_add_new_customers_button_clicked"
              onClick={() => {
                handleAddCustomer();
              }}
              disabled={loading}
            >
              {!loading && (
                <span className="indicator-label fs-16 fw-bold">
                  Add customers
                </span>
              )}
              {loading && (
                <span
                  className="indicator-progress fs-16 fw-bold"
                  style={{ display: 'block' }}
                >
                  Please wait...
                  <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                </span>
              )}
            </Button>
          </div>
        </Col>
      </Row>
    </div>
  );
};
export default AddNewCustomers;
